import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import {
  fetchGuardAppAccessRequests,
  phoneAppChipLines,
  relevantRequestForMember,
  type GuardAppAccessLoad,
  type GuardAppAccessRequestRow,
} from "../data/guard-app-access";
import { INVITE_ROLES, type InviteRole } from "../data/member-invites";
import {
  fetchMembers,
  SAFE_MEMBER_PROFILE_FAILURE,
  updateMemberProfile,
  updateMemberRole,
  type MemberRow,
} from "../data/members";
import { platformPeopleDisplayName } from "../data/platform-people-search";
import {
  removeAccountMember,
  restoreAccountMember,
  SAFE_MEMBER_LIFECYCLE_FAILURE,
  suspendAccountMember,
} from "../data/member-lifecycle";
import {
  clampSupportTtlMinutes,
  SUPPORT_SESSION_DEFAULT_TTL_MINUTES,
  SUPPORT_SESSION_MAX_TTL_MINUTES,
  SUPPORT_SESSION_MIN_TTL_MINUTES,
} from "../data/support-session";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";
import { guardRefTitle, resolveGuardName } from "../lib/guard-name";
import { useSupportSession } from "../support/SupportSessionProvider";
import { GuardAppAccessDrawer } from "./GuardAppAccessDrawer";

// Member directory — display names, work emails, and optional contact phones. Admins with
// member.manage can edit profiles inline (fixes seeded / legacy rows) and drive the Wave B
// lifecycle RPCs: suspend/restore, and soft remove (re-invite revives the same member).
// Every lifecycle action sits behind an explicit inline confirm with honest consequence
// copy, and success surfaces the server's released-assignment count so the reassign trail
// is visible. No client-side status writes exist; the RPCs are the only mutation path.
//
// This is also the entry point for a platform support session ("Support as…", product lock
// 2026-08-04). That affordance is rendered ONLY for a platform superadmin and ONLY on an active
// membership — a suspended, invited, or removed row never offers it, matching the server's own
// target rule (S4a). Non-superadmins never render the control and therefore never call the RPC.

export function MemberDirectory({
  client,
  accountId,
  timeZone,
  pollMs = 30000,
  canManage = false,
  canSupportAs = false,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
  canManage?: boolean;
  /** Platform superadmin only. Suppressed server-side while a support session is already open. */
  canSupportAs?: boolean;
}) {
  return (
    <MemberDirectorySurface
      client={client}
      accountId={accountId}
      timeZone={timeZone}
      pollMs={pollMs}
      canManage={canManage}
      canSupportAs={canSupportAs}
      redactContacts={false}
      showOwnerLabels={false}
      showGuardAppAccess
      onSupportStarted={undefined}
      onRowsSnapshot={undefined}
    />
  );
}

/**
 * Platform Accounts presentation of the same shipped member lifecycle. It deliberately keeps
 * contact details behind a per-row reveal, labels admin memberships as owners, and omits the
 * unrelated Guard app access workflow from this selected-workspace desk.
 */
export function PlatformMemberDirectory({
  client,
  accountId,
  timeZone,
  pollMs = 30000,
  canManage = false,
  canSupportAs = false,
  onSupportStarted,
  onRowsSnapshot,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
  canManage?: boolean;
  canSupportAs?: boolean;
  onSupportStarted?: () => void;
  onRowsSnapshot?: (rows: readonly MemberRow[] | null) => void;
}) {
  return (
    <MemberDirectorySurface
      client={client}
      accountId={accountId}
      timeZone={timeZone}
      pollMs={pollMs}
      canManage={canManage}
      canSupportAs={canSupportAs}
      redactContacts
      showOwnerLabels
      showGuardAppAccess={false}
      onSupportStarted={onSupportStarted}
      onRowsSnapshot={onRowsSnapshot}
    />
  );
}

function MemberDirectorySurface({
  client,
  accountId,
  timeZone,
  pollMs,
  canManage,
  canSupportAs,
  redactContacts,
  showOwnerLabels,
  showGuardAppAccess,
  onSupportStarted,
  onRowsSnapshot,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs: number;
  canManage: boolean;
  canSupportAs: boolean;
  redactContacts: boolean;
  showOwnerLabels: boolean;
  showGuardAppAccess: boolean;
  onSupportStarted: (() => void) | undefined;
  onRowsSnapshot: ((rows: readonly MemberRow[] | null) => void) | undefined;
}) {
  const { status, data, error, reload } = useReadModel<MemberRow[]>(
    () => fetchMembers(client, accountId),
    [client, accountId],
    pollMs,
  );
  // Guard app access requests (design lock 2026-08-09). Read only behind member.manage - the
  // same gate as the request RPCs - and quietly absent when the hosted RPCs are not deployed
  // yet, so the column never shows statuses nothing can load or save.
  const accessModel = useReadModel<GuardAppAccessLoad>(
    () =>
      canManage && showGuardAppAccess
        ? fetchGuardAppAccessRequests(client, accountId)
        : Promise.resolve({ status: "unavailable" as const }),
    [client, accountId, canManage, showGuardAppAccess],
    pollMs,
  );
  const [accessDrawerFor, setAccessDrawerFor] = useState<MemberRow | null>(null);

  useEffect(() => {
    onRowsSnapshot?.(status === "ready" ? (data ?? []) : null);
  }, [data, onRowsSnapshot, status]);

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading members…</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load the member directory."}
      </p>
    );
  }

  const rows = data ?? [];
  if (rows.length === 0) {
    return <p className="surface__state" role="status">No members yet.</p>;
  }

  const showActions = canManage || canSupportAs;
  const accessRows =
    accessModel.data !== null && accessModel.data.status === "ready" ? accessModel.data.rows : null;
  const showPhoneApp = showGuardAppAccess && canManage && accessRows !== null;

  return (
    <>
      <table className="member-directory">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Email</th>
            <th scope="col">Phone</th>
            <th scope="col">Role</th>
            <th scope="col">Status</th>
            {showPhoneApp ? <th scope="col">Phone app</th> : null}
            <th scope="col">Added</th>
            {showActions ? <th scope="col">Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <MemberDirectoryRow
              key={row.membershipId || `${row.memberRef ?? "member"}-${index}`}
              row={row}
              timeZone={timeZone}
              canManage={canManage}
              canSupportAs={canSupportAs}
              client={client}
              accountId={accountId}
              onSaved={reload}
              phoneAppRequest={
                accessRows !== null ? relevantRequestForMember(accessRows, row.membershipId) : null
              }
              showPhoneApp={showPhoneApp}
              redactContacts={redactContacts}
              showOwnerLabels={showOwnerLabels}
              onSupportStarted={onSupportStarted}
              onRequestAccess={() => setAccessDrawerFor(row)}
            />
          ))}
        </tbody>
      </table>
      {showPhoneApp ? (
        <p className="member-directory__phone-app-foot">
          <strong>Phone app</strong> shows where each person is with the Guard app. It is separate
          from their SecOps login. Officers cannot request access for themselves yet, so an admin
          sends it for them.
        </p>
      ) : null}
      {accessDrawerFor !== null && accessRows !== null ? (
        <GuardAppAccessDrawer
          client={client}
          accountId={accountId}
          member={accessDrawerFor}
          existing={relevantRequestForMember(accessRows, accessDrawerFor.membershipId)}
          onClose={() => setAccessDrawerFor(null)}
          onSaved={accessModel.reload}
        />
      ) : null}
    </>
  );
}

function MemberDirectoryRow({
  row,
  timeZone,
  canManage,
  canSupportAs,
  client,
  accountId,
  onSaved,
  phoneAppRequest,
  showPhoneApp,
  redactContacts,
  showOwnerLabels,
  onSupportStarted,
  onRequestAccess,
}: {
  row: MemberRow;
  timeZone: string;
  canManage: boolean;
  canSupportAs: boolean;
  client: SupabaseClient;
  accountId: string;
  onSaved: () => void;
  phoneAppRequest: GuardAppAccessRequestRow | null;
  showPhoneApp: boolean;
  redactContacts: boolean;
  showOwnerLabels: boolean;
  onSupportStarted: (() => void) | undefined;
  onRequestAccess: () => void;
}) {
  const resolved = resolveGuardName({
    displayName: row.memberDisplayName,
    label: row.memberLabel,
    ref: row.memberRef,
    fallback: "Member",
  });
  const renderedName = redactContacts
    ? platformPeopleDisplayName(resolved.name, row.membershipId, "Member")
    : resolved.name;
  const actionIdentity = redactContacts ? renderedName : (row.memberEmail ?? renderedName);
  const resolvedEditName = resolved.name.startsWith("Guard ") ? "" : resolved.name;
  const projectedEditName = redactContacts
    ? platformPeopleDisplayName(resolvedEditName, row.membershipId, "")
    : resolvedEditName;
  // Never place a malformed Platform label into an editable input. A projection that had to
  // mask contact data or refuse an opaque id starts blank so Save cannot persist the mask.
  const editableName = projectedEditName === resolvedEditName ? resolvedEditName : "";
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(editableName);
  const [draftPhone, setDraftPhone] = useState(row.contactPhone ?? "");
  const [draftRole, setDraftRole] = useState<InviteRole>(
    INVITE_ROLES.includes(row.role as InviteRole) ? (row.role as InviteRole) : "guard",
  );
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Wave B lifecycle: which consequence confirm is open, its optional reason, and the
  // post-action honesty note (released/retired counts from the server). "support" reuses the
  // same two-step confirm shape, with its own required reason and TTL.
  const [pendingAction, setPendingAction] = useState<"suspend" | "remove" | "support" | null>(null);
  const [lifecycleReason, setLifecycleReason] = useState("");
  const [lifecycleNote, setLifecycleNote] = useState<string | null>(null);
  const [supportTtl, setSupportTtl] = useState(String(SUPPORT_SESSION_DEFAULT_TTL_MINUTES));
  const [contactRevealed, setContactRevealed] = useState(false);
  const { start: startSupport } = useSupportSession();

  function startEdit(): void {
    setDraftName(editableName);
    setDraftPhone(row.contactPhone ?? "");
    setDraftRole(INVITE_ROLES.includes(row.role as InviteRole) ? (row.role as InviteRole) : "guard");
    setSaveError(null);
    setEditing(true);
  }

  async function runLifecycle(action: "suspend" | "remove" | "restore"): Promise<void> {
    if (busy || row.membershipId.length === 0) return;
    setBusy(true);
    setSaveError(null);
    setLifecycleNote(null);
    try {
      if (action === "restore") {
        const outcome = await restoreAccountMember(client, accountId, row.membershipId);
        if (!outcome.ok) {
          setSaveError(outcome.reason);
          return;
        }
        setLifecycleNote("Member restored to active.");
      } else {
        const reason = lifecycleReason.trim().length > 0 ? lifecycleReason.trim() : null;
        const outcome =
          action === "suspend"
            ? await suspendAccountMember(client, accountId, row.membershipId, reason)
            : await removeAccountMember(client, accountId, row.membershipId, reason);
        if (!outcome.ok) {
          setSaveError(outcome.reason);
          return;
        }
        const released = outcome.scheduleAssignmentsReleased;
        setLifecycleNote(
          `${action === "suspend" ? "Member suspended." : "Member removed."} Released ${released} future assignment${released === 1 ? "" : "s"} to reassign; push tokens retired.`,
        );
      }
      setPendingAction(null);
      setLifecycleReason("");
      onSaved();
    } catch {
      setSaveError(SAFE_MEMBER_LIFECYCLE_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Start a support session as this membership. The adapter refuses a blank/short reason before
   * the wire and the server refuses everything that actually matters; on success the shell picks
   * the session up, pins the workspace, and re-reads permissions — nothing is assumed here.
   */
  async function runSupport(): Promise<void> {
    if (busy || row.membershipId.length === 0) return;
    setBusy(true);
    setSaveError(null);
    setLifecycleNote(null);
    try {
      const outcome = await startSupport({
        accountId,
        membershipId: row.membershipId,
        reason: lifecycleReason,
        ttlMinutes: clampSupportTtlMinutes(supportTtl),
      });
      if (!outcome.ok) {
        setSaveError(outcome.reason);
        return;
      }
      setPendingAction(null);
      setLifecycleReason("");
      setSupportTtl(String(SUPPORT_SESSION_DEFAULT_TTL_MINUTES));
      onSupportStarted?.();
    } finally {
      setBusy(false);
    }
  }

  async function saveProfile(): Promise<void> {
    if (busy || row.membershipId.length === 0) return;
    const trimmedName = draftName.trim();
    if (trimmedName.length === 0) {
      setSaveError("Display name is required.");
      return;
    }
    setBusy(true);
    setSaveError(null);
    try {
      const profileOutcome = await updateMemberProfile(
        client,
        accountId,
        row.membershipId,
        trimmedName,
        draftPhone.trim().length > 0 ? draftPhone.trim() : null,
      );
      if (!profileOutcome.ok) {
        setSaveError(profileOutcome.reason);
        return;
      }

      if (draftRole !== row.role) {
        const roleOutcome = await updateMemberRole(client, accountId, row.membershipId, draftRole);
        if (!roleOutcome.ok) {
          setSaveError(roleOutcome.reason);
          return;
        }
      }

      setEditing(false);
      onSaved();
    } catch {
      setSaveError(SAFE_MEMBER_PROFILE_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr
      className="member-directory__row"
      id={!redactContacts && row.membershipId ? `member-${row.membershipId}` : undefined}
    >
      <td className="member-directory__member" data-label="Name">
        {editing ? (
          <label className="member-directory__edit-field">
            <span className="member-directory__edit-label">Display name</span>
            <input
              type="text"
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              aria-label={`Display name for ${actionIdentity}`}
              disabled={busy}
            />
          </label>
        ) : (
          <span title={redactContacts ? undefined : guardRefTitle(resolved.ref)}>{renderedName}</span>
        )}
      </td>
      <td className="member-directory__email" data-label="Email">
        <span>{contactValue(row.memberEmail, "email", redactContacts, contactRevealed)}</span>
        {redactContacts && (row.memberEmail !== null || row.contactPhone !== null) ? (
          <button
            type="button"
            className="member-directory__reveal"
            aria-pressed={contactRevealed}
            onClick={() => setContactRevealed((revealed) => !revealed)}
          >
            {contactRevealed ? "Hide contact" : "Reveal contact"}
          </button>
        ) : null}
      </td>
      <td className="member-directory__phone" data-label="Phone">
        {editing && (!redactContacts || contactRevealed) ? (
          <label className="member-directory__edit-field">
            <span className="member-directory__edit-label">Phone (optional)</span>
            <input
              type="tel"
              value={draftPhone}
              onChange={(event) => setDraftPhone(event.target.value)}
              placeholder="(502) 555-0100"
              aria-label={`Phone for ${actionIdentity}`}
              disabled={busy}
            />
          </label>
        ) : (
          contactValue(row.contactPhone, "phone", redactContacts, contactRevealed)
        )}
      </td>
      <td className="member-directory__role" data-label="Role">
        {editing ? (
          <label className="member-directory__edit-field">
            <span className="member-directory__edit-label">Role</span>
            <select
              value={draftRole}
              onChange={(event) => setDraftRole(event.target.value as InviteRole)}
              aria-label={`Role for ${actionIdentity}`}
              disabled={busy}
            >
              {INVITE_ROLES.map((inviteRole) => (
                <option key={inviteRole} value={inviteRole}>
                  {humanizeLabel(inviteRole)}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <span className="member-directory__role-reading">
            <span>{humanizeLabel(row.role)}</span>
            {showOwnerLabels && row.role === "admin" ? (
              <span className="member-directory__owner">Owner</span>
            ) : null}
          </span>
        )}
      </td>
      <td className="member-directory__status-cell" data-label="Status">
        <span className={`member-directory__status member-directory__status--${row.status}`}>
          {humanizeLabel(row.status)}
        </span>
      </td>
      {showPhoneApp ? (
        <td className="member-directory__phone-app" data-label="Phone app">
          {phoneAppRequest === null ? (
            <span className="gaa-chip gaa-chip--none">Not requested</span>
          ) : (
            phoneAppChipLines(phoneAppRequest).map((chip) => (
              <span key={chip.label} className={`gaa-chip gaa-chip--${chip.tone}`}>
                {chip.label}
              </span>
            ))
          )}
        </td>
      ) : null}
      <td className="member-directory__added" data-label="Added">
        {formatDisplayTime(row.createdAt, timeZone)}
      </td>
      {canManage || canSupportAs ? (
        <td className="member-directory__actions" data-label="Actions">
          {editing ? (
            <div className="member-directory__edit-actions">
              <button type="button" className="builder__navbtn" disabled={busy} onClick={() => void saveProfile()}>
                Save
              </button>
              <button
                type="button"
                className="builder__cancel"
                disabled={busy}
                onClick={() => {
                  setEditing(false);
                  setDraftName(editableName);
                  setDraftPhone(row.contactPhone ?? "");
                  setDraftRole(
                    INVITE_ROLES.includes(row.role as InviteRole) ? (row.role as InviteRole) : "guard",
                  );
                  setSaveError(null);
                }}
              >
                Cancel
              </button>
              {saveError !== null ? (
                <p className="surface__state surface__state--error" role="alert">{saveError}</p>
              ) : null}
            </div>
          ) : pendingAction === "support" ? (
            <div className="member-directory__support-confirm">
              <p className="member-directory__lifecycle-copy">
                The console will act as {renderedName} ({humanizeLabel(row.role)}) on this
                account. Your platform elevation is suspended for the whole session, so you see
                exactly what they see — no more. Everything you do is recorded against your own
                account. It ends when you choose Exit support, or automatically when the time
                below runs out.
              </p>
              <input
                type="text"
                value={lifecycleReason}
                onChange={(event) => setLifecycleReason(event.target.value)}
                placeholder="Reason (required)"
                aria-label={`Reason for supporting as ${actionIdentity}`}
                disabled={busy}
              />
              <label className="member-directory__edit-field">
                <span className="member-directory__edit-label">
                  Minutes ({SUPPORT_SESSION_MIN_TTL_MINUTES}–{SUPPORT_SESSION_MAX_TTL_MINUTES})
                </span>
                <input
                  type="number"
                  min={SUPPORT_SESSION_MIN_TTL_MINUTES}
                  max={SUPPORT_SESSION_MAX_TTL_MINUTES}
                  value={supportTtl}
                  onChange={(event) => setSupportTtl(event.target.value)}
                  aria-label={`Support session minutes for ${actionIdentity}`}
                  disabled={busy}
                />
              </label>
              <button
                type="button"
                className="builder__cancel"
                disabled={busy}
                onClick={() => void runSupport()}
              >
                {busy ? "Starting…" : `Support as ${renderedName}`}
              </button>
              <button
                type="button"
                className="builder__navbtn"
                disabled={busy}
                onClick={() => {
                  setPendingAction(null);
                  setLifecycleReason("");
                  setSupportTtl(String(SUPPORT_SESSION_DEFAULT_TTL_MINUTES));
                  setSaveError(null);
                }}
              >
                Cancel
              </button>
              {saveError !== null ? (
                <p className="surface__state surface__state--error" role="alert">{saveError}</p>
              ) : null}
            </div>
          ) : pendingAction !== null ? (
            <div className="member-directory__lifecycle-confirm">
              <p className="member-directory__lifecycle-copy">
                {pendingAction === "suspend"
                  ? "Suspending blocks access at their next request, retires push tokens, and releases future assignments for reassignment. Nothing is deleted."
                  : "Removing frees future assignments to reassign and keeps all history. Re-inviting this email later revives the same member."}
              </p>
              <input
                type="text"
                value={lifecycleReason}
                onChange={(event) => setLifecycleReason(event.target.value)}
                placeholder="Reason (optional)"
                aria-label={`Reason for ${pendingAction} of ${actionIdentity}`}
                disabled={busy}
              />
              <button
                type="button"
                className="builder__cancel"
                disabled={busy}
                onClick={() => void runLifecycle(pendingAction)}
              >
                {busy
                  ? "Working…"
                  : pendingAction === "suspend"
                    ? "Confirm suspend"
                    : "Confirm remove"}
              </button>
              <button
                type="button"
                className="builder__navbtn"
                disabled={busy}
                onClick={() => {
                  setPendingAction(null);
                  setLifecycleReason("");
                  setSaveError(null);
                }}
              >
                Keep member
              </button>
              {saveError !== null ? (
                <p className="surface__state surface__state--error" role="alert">{saveError}</p>
              ) : null}
            </div>
          ) : (
            <div className="member-directory__row-actions">
              {/* Active memberships only: the request RPC refuses anything else, so the button
                  never promises a refusal. An open request flips the label to View request. */}
              {canManage && showPhoneApp && row.status === "active" ? (
                <button type="button" className="builder__navbtn" onClick={onRequestAccess}>
                  {phoneAppRequest !== null && phoneAppRequest.status !== "closed"
                    ? "View request"
                    : "Request access"}
                </button>
              ) : null}
              {canManage ? (
                <button type="button" className="builder__navbtn" onClick={startEdit}>
                  Edit
                </button>
              ) : null}
              {canManage && row.status === "active" ? (
                <button
                  type="button"
                  className="builder__cancel"
                  disabled={busy}
                  onClick={() => {
                    setSaveError(null);
                    setLifecycleNote(null);
                    setPendingAction("suspend");
                  }}
                >
                  Suspend
                </button>
              ) : null}
              {canManage && row.status === "suspended" ? (
                <button
                  type="button"
                  className="builder__navbtn"
                  disabled={busy}
                  onClick={() => void runLifecycle("restore")}
                >
                  Restore
                </button>
              ) : null}
              {canManage && (row.status === "active" || row.status === "suspended") ? (
                <button
                  type="button"
                  className="builder__cancel"
                  disabled={busy}
                  onClick={() => {
                    setSaveError(null);
                    setLifecycleNote(null);
                    setPendingAction("remove");
                  }}
                >
                  Remove
                </button>
              ) : null}
              {/* Active memberships only — S4a refuses suspended / invited / removed targets,
                  so offering the control on those rows would only promise a server refusal. */}
              {canSupportAs && row.status === "active" ? (
                <button
                  type="button"
                  className="builder__navbtn"
                  disabled={busy}
                  onClick={() => {
                    setSaveError(null);
                    setLifecycleNote(null);
                    setLifecycleReason("");
                    setSupportTtl(String(SUPPORT_SESSION_DEFAULT_TTL_MINUTES));
                    setPendingAction("support");
                  }}
                >
                  Support as…
                </button>
              ) : null}
              {saveError !== null ? (
                <p className="surface__state surface__state--error" role="alert">{saveError}</p>
              ) : null}
              {lifecycleNote !== null ? (
                <p className="surface__state" role="status">{lifecycleNote}</p>
              ) : null}
            </div>
          )}
        </td>
      ) : null}
    </tr>
  );
}

function contactValue(
  value: string | null,
  kind: "email" | "phone",
  redact: boolean,
  revealed: boolean,
): string {
  if (value === null) return "Not provided";
  if (!redact || revealed) return value;
  if (kind === "phone") {
    const tail = value.replace(/\D/gu, "").slice(-4);
    return tail.length > 0 ? `••• ${tail}` : "•••";
  }
  const separator = value.indexOf("@");
  if (separator <= 0) return "•••";
  return `${value.slice(0, 1)}•••${value.slice(separator)}`;
}
